/**
 * Product page behaviour: media gallery, variant picker, quantity stepper.
 *
 * Three custom elements rather than one section controller, because the theme editor
 * can add, remove and reorder these blocks independently. An element that upgrades on
 * connection needs no re-initialisation hook: `shopify:section:load` replaces the
 * markup, the browser upgrades the new elements, and nothing has to remember to run.
 */

/**
 * The gallery is one track at every width, driven entirely by CSS breakpoints rather
 * than by a mode this element chooses. On a phone the shopper swipes it; from 1024px
 * it becomes a static grid of every photograph, and from 1280px the first photograph
 * additionally mirrors into a sticky preview column beside that grid (`.gallery__preview`
 * — see `component-product.css`). Scroll position is the source of truth for which
 * image is "current" below 1024px; above it every slide is on screen at once, so
 * `select()` instead scrolls the target into view only if the page has carried it off
 * screen, and `current`/`markCurrent` exist mainly to drive the sticky preview mirror
 * and the lightbox's starting slide.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.preview = this.querySelector('.gallery__preview-image');

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.select(Number(thumb.dataset.galleryThumb) - 1));
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.select(index));
    });

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

    /* The lightbox is a native <dialog>: modality, focus trapping, the backdrop and
       dismissal on Escape are all behaviours the platform already implements correctly,
       and every hand-rolled overlay in a theme is a re-implementation of them that is
       slightly wrong. */
    this.dialog = this.querySelector('[data-gallery-lightbox]');
    this.lightboxTrack = this.dialog?.querySelector('.lightbox__track');
    this.lightboxSlides = Array.from(this.dialog?.querySelectorAll('[data-lightbox-slide]') ?? []);
    /* Below 1024px there is one photograph on screen, so the single corner button opens
       it. From 1024px every photograph is on screen at once, so each carries its own
       `[data-gallery-open]` trigger instead — both call the same method, just with a
       different index. */
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.openLightbox());
    this.querySelectorAll('[data-gallery-open]').forEach((button) => {
      button.addEventListener('click', () => this.openLightbox(Number(button.dataset.galleryOpen)));
    });
    this.dialog?.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    /* A click on the backdrop lands on the dialog itself, never on its contents. */
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    /* Swipe covers touch; these two cover everything else. Without them a mouse user
       who opens the lightbox has no way to see any photograph but the one they opened
       it on — see the comment above the buttons in `product-gallery.liquid`. */
    this.dialog?.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.stepLightbox(-1));
    this.dialog?.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.stepLightbox(1));
    this.dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      if (event.key === 'ArrowRight') this.stepLightbox(1);
    });

    /* The scroller reports its own position rather than being told: a swipe, an arrow
       and a thumbnail click all end in the same place. Above 1024px every slide
       intersects at once; whichever fires last "wins" the current class, which is
       harmless since nothing below 1024px reads it and the mark is essentially cosmetic
       once the track stops swiping. */
    this.observer = new IntersectionObserver(
      (entries) => {
        /* Meaningful only while the track is the swiping filmstrip, below 1024px. From
           1024px the track becomes a static grid where every slide is on screen at once,
           so most or all entries report `isIntersecting` in the same batch — "whichever
           fires last wins" was a harmless tie-break when nothing above 1024px read the
           mark, but the sticky preview mirror (`updatePreview`, 1280px+) does read it, and
           without this guard it overwrites the correct first photograph Liquid already
           rendered there with whichever slide happens to sort last in the batch (observed
           live: `kingsons-backpack`'s preview showed its last photograph instead of its
           first). Explicit callers — `select()` from a thumbnail click, `selectByMediaId()`
           from a variant swap — still update the preview correctly at any width; only this
           passive scroll-position signal needs to stand down once the track stops
           scrolling. */
        if (window.matchMedia('(min-width: 1024px)').matches) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.markCurrent(this.slides.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get current() {
    return this.slides.findIndex((slide) => slide.classList.contains('is-current'));
  }

  step(delta) {
    const next = Math.min(this.slides.length - 1, Math.max(0, this.current + delta));
    this.select(next);
  }

  select(index) {
    const slide = this.slides[index];
    if (!slide) return;

    /* From 1024px the track is a static grid — every slide is already visible, so there
       is nothing to scroll to horizontally. The one exception is a page tall enough that
       the target slide has scrolled off screen vertically, which `scrollIntoView` alone
       corrects without moving the page when the slide is already in view. Below 1024px
       the track is still the swiping filmstrip, where `scrollIntoView` on the slide
       would also scroll the page itself, so the scroller is moved directly instead. */
    if (getComputedStyle(this.track).display === 'grid') {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    } else {
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    /* Marked directly rather than left to the IntersectionObserver: a grid slide that is
       already fully on screen never fires an "entering" event, so the sticky preview and
       the lightbox's starting slide would otherwise never learn a variant changed them. */
    this.markCurrent(index);
  }

  /** Scrolls to whichever slide carries a media id, for variant-linked images. */
  selectByMediaId(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0 && index !== this.current) this.select(index);
  }

  /** Opens the full-size view on the given slide, or on whichever one the scroller is
   *  currently resting on when no index is given (the single corner button below 1024px,
   *  which has no index of its own to pass). */
  openLightbox(index) {
    if (!this.dialog) return;
    const resolvedIndex = Math.max(0, index ?? this.current);
    this.markPendingSlides();
    this.dialog.showModal();
    const slide = this.dialog.querySelectorAll('[data-lightbox-slide]')[resolvedIndex];
    /* `auto`, not `smooth`: the dialog has only just been painted, and animating to the
       right image from the first one shows the wrong product for a moment. */
    if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
  }

  /** Moves the lightbox one photograph left or right. Scroll position is the source of
   *  truth here too, same as the main track in `select()` — every slide is exactly one
   *  track-width wide (`flex: 0 0 100%`), so the current index is just how many widths
   *  the scroller has travelled. */
  stepLightbox(delta) {
    if (!this.lightboxTrack || !this.lightboxSlides.length) return;
    const width = this.lightboxTrack.clientWidth;
    if (!width) return;
    const current = Math.round(this.lightboxTrack.scrollLeft / width);
    const next = Math.min(this.lightboxSlides.length - 1, Math.max(0, current + delta));
    this.lightboxTrack.scrollTo({ left: next * width, behavior: 'smooth' });
  }

  /** Flags every lightbox slide whose image has not arrived, so the panel shows a loading
   *  bar rather than an empty frame.
   *
   *  Run just before the dialog opens, because that is the moment the request starts: the
   *  images are `loading="lazy"` inside a closed <dialog>, which is never visible, so
   *  nothing has been fetched up to this point no matter how long the page has been open.
   *  These are the only images in the theme that behave this way — see the
   *  `.lightbox__slide.is-loading` block in `component-product.css` for why ordinary
   *  product images deliberately have no equivalent. */
  markPendingSlides() {
    this.dialog.querySelectorAll('[data-lightbox-slide]').forEach((slide) => {
      const image = slide.querySelector('img');
      /* Already flagged means a previous open is still waiting on the same request, and
         its listeners are still attached. */
      if (!image || image.complete || slide.classList.contains('is-loading')) return;

      slide.classList.add('is-loading');
      /* `error` as well as `load`: a request that fails still has to clear the bar, or the
         slide sits animating behind an image that is never coming. */
      const clear = () => slide.classList.remove('is-loading');
      image.addEventListener('load', clear, { once: true });
      image.addEventListener('error', clear, { once: true });
    });
  }

  /** Mirrors a slide's image into the sticky preview column (1280px+ only — see
   *  `.gallery__preview` in `component-product.css`). A no-op everywhere else, since
   *  `this.preview` is only ever found when the markup renders it. */
  updatePreview(index) {
    if (!this.preview) return;
    const image = this.slides[index]?.querySelector('img');
    if (!image) return;
    /* `srcset` carries the candidates, which are the same set wherever the photograph is
       shown. `sizes` deliberately does not travel with it: the slide is a cell in a
       two-column grid and the preview is the wide column beside it, so copying the slide's
       hint would have the preview download a picture sized for something a third its width
       and then stretch it. The preview keeps its own. */
    if (image.srcset) this.preview.srcset = image.srcset;
    this.preview.src = image.currentSrc || image.src;
    this.preview.alt = image.alt;
  }

  markCurrent(index) {
    if (index < 0) return;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-current', i === index));
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    this.updatePreview(index);
  }
}

/**
 * The variant picker.
 *
 * Its whole job is the sparse matrix. Two products here offer 11 of 16 and 8 of 12
 * combinations, so after each change the values are re-tested and marked in one of three
 * states: available, sold out, or unavailable — the last meaning no such variant exists,
 * which is disabled outright.
 *
 * Which options get re-tested is the reference's cascade, and the snippet's `{% doc %}`
 * carries the evidence for it: each option is tested against the options *above* it only.
 * The first option is therefore never disabled, and a shopper can always reach any value
 * of it in one click — the options below it move to keep the selection real.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variant-data]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch {
      /* A malformed table must not take the add-to-cart button down with it: leaving
         the picker inert still posts the server-rendered variant id. */
      return;
    }

    this.idInput = this.querySelector('[data-variant-id]');
    this.fieldsets = Array.from(this.querySelectorAll('[data-option-index]')).filter(
      (el) => el.tagName === 'FIELDSET'
    );

    this.addEventListener('change', (event) => {
      if (event.target.matches('.variant-picker__input')) {
        this.onChange();
      }
    });

    /* Resolved from the parent, not from `this`: the picker carries a `data-section-id` of
       its own (it needs the id to build the section-render URL), so `this.closest()` matches
       the picker itself and every mirror below is then looked up inside the picker, where
       none of them live — the price, the inventory line and the sticky bar all sit outside
       it in the section wrapper. */
    this.section = this.parentElement?.closest('[data-section-id]') ?? null;

    /* Reconcile once, because the radios are live before this element is.

       The option inputs are ordinary form controls in the server-rendered HTML, clickable
       from first paint; this custom element only upgrades when its module has downloaded
       and run. On a slow connection that window is seconds wide, and a shopper who picks a
       Model in it gets a swatch that moves and nothing else: no `change` listener existed
       yet, so the price, the `?variant=` URL and — the part that matters — the hidden
       variant id all stay on the variant the server rendered. Add to cart then posts the
       wrong one, silently, with the right one shown as selected. Reproduced 4 runs of 4 at
       1.6 Mbps / 250 ms: swatch read "iPhone 17 Air", the form still carried the iPhone 17
       id.

       Making whatever the radios say at upgrade time the truth is the whole fix. Guarded
       on a real mismatch so the ordinary load pays nothing for it: `onChange` re-renders
       the price block over the network, and on every load where nobody beat the script
       the id already agrees. */
    const rendered = this.variants.find((v) => v.options.every((opt, i) => opt === this.selection[i]));
    if (rendered && this.idInput && String(rendered.id) !== this.idInput.value) this.onChange();
  }

  get selection() {
    return this.fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('.variant-picker__input:checked');
      return checked ? checked.value : null;
    });
  }

  onChange() {
    /* The selection can move under us: a value the change made impossible is replaced by
       one that exists, so the variant is resolved from what `refreshStates` settled on
       rather than from what was clicked. */
    const selection = this.refreshStates();

    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selection[i]));
    this.updateForm(variant);
    this.updateUrl(variant);
    this.updateMedia(variant);
  }

  /**
   * Marks every value of every option against the options above it.
   * A value is *unavailable* when no variant pairs it with those selections, and
   * *sold out* when one exists but cannot be bought.
   */
  refreshStates() {
    /* Walked in order, because the cascade only looks upward: by the time an option is
       marked, every option above it has settled — including any that had to move off a
       value this change made impossible. */
    const selection = this.selection;

    this.fieldsets.forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionIndex);

      fieldset.querySelectorAll('.variant-picker__input').forEach((input) => {
        /* Only the options above this one constrain it — the cascade. An option is
           tested against what the shopper settled before reaching it, and never against
           what sits below, which is free to move to follow this choice. */
        const matches = this.variants.filter(
          (v) =>
            v.options[index] === input.value &&
            v.options.every((opt, i) => i >= index || selection[i] === null || opt === selection[i])
        );

        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (!label) return;

        if (matches.length === 0) {
          input.disabled = true;
          label.dataset.state = 'unavailable';
        } else {
          input.disabled = false;
          if (matches.some((v) => v.available)) delete label.dataset.state;
          else label.dataset.state = 'sold-out';
        }
      });

      /* A choice above can strand this option on a value that no longer exists — picking
         "iPhone 17 Air" while "Blue" is selected, when no Air is made in blue. Move it to
         the first value that does exist, and carry that forward, so the options below
         narrow against what is really selected rather than against what was. */
      const stranded = fieldset.querySelector('.variant-picker__input:checked');
      if (stranded && stranded.disabled) {
        const fallback = fieldset.querySelector('.variant-picker__input:not([disabled])');
        if (fallback) fallback.checked = true;
      }

      const settled = fieldset.querySelector('.variant-picker__input:checked');
      selection[index] = settled ? settled.value : null;

      const echo = fieldset.querySelector('[data-selected-value]');
      if (settled && echo) echo.textContent = settled.value;
    });

    return selection;
  }

  updateForm(variant) {
    const section = this.section;
    if (!section) return;

    if (this.idInput) this.idInput.value = variant ? variant.id : '';

    /* Every add button, not the first: the sticky buy bar carries a mirror of it, and a
       mirror that still says "Add to cart" for a combination that does not exist is a
       purchase the shopper is invited to attempt and cannot complete. */
    section.querySelectorAll('[data-add-button]').forEach((button) => {
      /* Labels come from each button's own data attributes, so every user-facing string
         still lives in `locales/en.default.json` and none is written in JavaScript. */
      const labels = button.dataset;
      const buttonText = button.querySelector('[data-add-button-text]');

      button.disabled = !variant || !variant.available;

      /* The inline price (main button only — see product.liquid) restates a number the
         label already explains away when nothing can be bought, so it hides with the
         same condition that disables the button. */
      const priceWrap = button.querySelector('[data-button-price-target]');
      const separator = button.querySelector('.product__submit-sep');
      const showPrice = Boolean(variant && variant.available);
      if (priceWrap) priceWrap.hidden = !showPrice;
      if (separator) separator.hidden = !showPrice;

      if (!buttonText) return;

      if (!variant) buttonText.textContent = labels.labelUnavailable || buttonText.textContent;
      else if (variant.available) buttonText.textContent = labels.labelAdd || buttonText.textContent;
      else buttonText.textContent = labels.labelSoldOut || buttonText.textContent;
    });

    const dynamicCheckout = section.querySelector('[data-payment-button]');
    if (dynamicCheckout) dynamicCheckout.hidden = !variant || !variant.available;

    /* The reference does not leave a greyed-out "Sold out" button sitting there: while a
       real variant is out of stock it swaps that button for the back-in-stock opener, and
       swaps it back the moment a purchasable one is selected. The condition is a variant
       that exists but cannot be bought — a combination no variant covers has no stock to
       be notified about, so that case keeps the disabled "Unavailable" button instead.
       Only the main submit is swapped, never the sticky bar's mirror, which is why the
       hook is `[data-primary-submit]` and not `[data-add-button]`. */
    const alertButton = section.querySelector('[data-alert-button]');
    if (alertButton) {
      const outOfStock = Boolean(variant && !variant.available);
      alertButton.hidden = !outOfStock;
      const primary = section.querySelector('[data-primary-submit]');
      if (primary) primary.hidden = outOfStock;

      /* Rewritten rather than left at the value the page rendered with, so the alert the
         merchant receives names the variant that was actually selected. */
      const message = section.querySelector('[data-alert-message]');
      if (message && variant) {
        const url = new URL(window.location.href);
        url.search = '';
        url.searchParams.set('variant', variant.id);
        const title = message.dataset.productTitle || '';
        const suffix = variant.title && variant.title !== 'Default Title' ? ' \u2014 ' + variant.title : '';
        message.value = title + suffix + ' ' + url.toString();
      }
    }

    const sku = section.querySelector('[data-sku-target]');
    if (sku && variant) sku.textContent = variant.sku;

    /* Price and stock come back from the server rather than being formatted here: the
       money format is a store setting, and reimplementing it in JavaScript is how a
       theme ends up showing two different prices for one variant. */
    if (variant) this.fetchSection(variant.id);
  }

  async fetchSection(variantId) {
    const section = this.section;
    const sectionId = section?.dataset.sectionId;
    if (!sectionId) return;

    const url = `${this.dataset.url}?variant=${variantId}&section_id=${sectionId}`;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      /* Every mirror of a target is refreshed, not just the first: the sticky buy bar
         carries a second price element under the same hook, and updating one of the two
         is how a page ends up showing a stale price beside a fresh one.

         The bar's variant line and thumbnail ride the same fetch. The reference writes
         both from the client (`updateProductOptions` sets `variant.title`,
         `updateProductMedia` builds a new `<img>` and its srcset by hand); taking them
         from the render that is already in flight costs nothing and keeps one source for
         what the bar says. */
      [
        '[data-price-target]',
        '[data-inventory-target]',
        '[data-button-price-target]',
        '[data-sticky-options-target]',
        '[data-sticky-media-target]',
      ].forEach((selector) => {
        const fresh = html.querySelector(selector);
        if (!fresh) return;
        section.querySelectorAll(selector).forEach((live) => {
          live.innerHTML = fresh.innerHTML;
        });
      });
    } catch {
      /* An offline or throttled request leaves the previous price on screen, which is
         stale but never wrong-by-invention. The form still posts the right variant. */
    }
  }

  updateUrl(variant) {
    if (!variant || !this.dataset.url) return;
    /* replaceState, not pushState: a shopper cycling through four colours should not
       have to press back four times to leave the page. */
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  updateMedia(variant) {
    if (!variant || !variant.media_id) return;
    this.section?.querySelector('product-gallery')?.selectByMediaId(variant.media_id);
  }
}

/**
 * Quantity stepper. The input is the control; the buttons are conveniences on top.
 *
 * The reference disables the minus button the moment quantity is at its floor (its own
 * captured `buttons.json` shows `"name":"minus","disabled":true` at the default qty of
 * 1) and, symmetrically, the plus button at whatever ceiling the input carries. Neither
 * button had that state here — both stayed clickable and full-opacity at the boundary,
 * which is silent to a keyboard/screen-reader user (no `disabled`, no `aria-disabled`)
 * as well as a visual mismatch. `step()` already clamped the value; this only makes the
 * clamp visible before the click that would no-op.
 */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.quantity__input');
    if (!this.input) return;

    this.down = this.querySelector('[data-quantity-down]');
    this.up = this.querySelector('[data-quantity-up]');
    this.down?.addEventListener('click', () => this.step(-1));
    this.up?.addEventListener('click', () => this.step(1));
    this.input.addEventListener('change', () => this.syncButtons());
    this.input.addEventListener('input', () => this.syncButtons());
    this.syncButtons();
  }

  step(delta) {
    const min = Number(this.input.min) || 1;
    const max = this.input.max === '' ? Infinity : Number(this.input.max);
    const next = Math.min(max, Math.max(min, (Number(this.input.value) || min) + delta));
    if (next === Number(this.input.value)) return;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncButtons() {
    const min = Number(this.input.min) || 1;
    const max = this.input.max === '' ? Infinity : Number(this.input.max);
    const value = Number(this.input.value) || min;
    if (this.down) this.down.disabled = value <= min;
    if (this.up) this.up.disabled = value >= max;
  }
}

/**
 * The add-to-cart form.
 *
 * The form is submitted over `fetch` rather than by navigation, for one reason: the cart
 * drawer. A native POST to /cart/add ends on the cart page, which throws the shopper out
 * of the product they were reading; the theme's contract is that adding an item updates
 * the cart in place and announces it.
 *
 * The announcement is `hubcart:cart:updated` on `document`, carrying the whole cart. The
 * drawer owns what happens next — this element never opens, renders or reads it, so the
 * two can be worked on independently and neither has to know the other's markup.
 *
 * The button's label never changes for a request in flight. Measured on the reference
 * (`CONCEPT_FORENSICS_COMMERCE/products/ugreen-hdmi/desktop/interaction-log.json`): its
 * add-to-cart button reads exactly "Add to cart  -  $7.00" before the click, mid-request,
 * after the item lands, and with the cart drawer open — four captured states, one string.
 * Progress is communicated by `aria-busy` alone, which the shared `.button` loader
 * (`assets/base.css`, `assets/theme.js`) already renders as a cross-fade to three dots;
 * success is the cart drawer opening, which this element neither draws nor waits for.
 * A label swap is kept only for the *sold out* / *unavailable* text, in
 * `VariantPicker.updateForm` below — that is a property of the selected variant, not of
 * this request, and the reference swaps it too (its button carries a
 * `data-sold-out="Sold Out"` hook of its own).
 *
 * Every label still comes off the button's own data attributes, so no user-facing English
 * is written in JavaScript and all of it stays in the locale files.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.errorTarget = this.querySelector('[data-add-error]');

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    /* Without the endpoints there is nothing to fetch, so the native POST is left to
       happen: a slower path to the cart is still a working one. */
    if (!this.dataset.addUrl || !this.dataset.cartUrl) return;
    event.preventDefault();
    if (this.busy) return;

    this.busy = true;
    this.setBusy(true);
    this.clearError();

    try {
      const body = new FormData(this.form);
      /* Asking for the cart back in the same request would save a round trip, but the
         sections it returns are rendered against the drawer's markup, which belongs to
         another component. Fetching /cart.js afterwards keeps this element's output to
         plain cart data that anything can read. */
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const result = await response.json();

      if (!response.ok) {
        /* Shopify's own message, already localised, and specific: "You can only add 2 of
           this item to your cart" is worth far more than a generic failure line. */
        this.showError(result.description || result.message);
        return;
      }

      const cart = await fetch(this.dataset.cartUrl).then((r) => r.json());
      document.dispatchEvent(
        new CustomEvent('hubcart:cart:updated', { bubbles: true, detail: { cart } })
      );
    } catch {
      /* An offline or blocked request must not leave a permanently spinning button, and
         it must not claim an item was added. */
      this.showError(this.querySelector('[data-add-button]')?.dataset.labelError);
    } finally {
      this.busy = false;
      this.setBusy(false);
    }
  }

  setBusy(isBusy) {
    this.querySelectorAll('[data-add-button]').forEach((button) => {
      button.disabled = isBusy;
      /* No class, no label swap — `aria-busy` is the whole of the progress state, and
         the shared button styling in `assets/base.css` reveals its own loader from it. */
      if (isBusy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  showError(message) {
    if (!this.errorTarget || !message) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.errorTarget) return;
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = '';
  }
}

/**
 * The sticky buy bar.
 *
 * Two scopes decide whether it is on screen, not one — the reference's own pair. It
 * appears once the whole product form has scrolled off the top (`data-watch`), and it
 * withdraws again when a yielding region arrives (`data-watch-until`): a bar pinned over
 * the footer is chrome with nothing left to interrupt. Watching only the buy button, as an
 * earlier revision did, gave the first half of that and none of the second.
 *
 * `data-watch-until` is a selector *list*, and the bar withdraws while any of its matches
 * is on screen, so a page that grows a second yielding region adds it there rather than
 * being special-cased here. Today the footer is the only entry: it is the only region that
 * genuinely ends the bar's usefulness. The FAQ contact form was tried in this list and
 * removed — being sticky, it intersects for its whole section, which suppressed the bar
 * across the entire lower page. That collision is handled where it belongs, in CSS keyed
 * on the form having focus; see `.sticky-buy__card` in `component-product.css`.
 *
 * It owns no form of its own. Its button is associated with the main form through the
 * `form` attribute, so the variant, the quantity and the submit handler are the same ones
 * the page already has — there is no second copy of the state to drift.
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const from = this.dataset.watch ? document.querySelector(this.dataset.watch) : null;
    const until = this.dataset.watchUntil ? [...document.querySelectorAll(this.dataset.watchUntil)] : [];
    if (!from) return;

    /* The yielding regions currently on screen, rather than a single boolean: with more
       than one sentinel a shared flag would be overwritten by whichever entry the observer
       reported last, so a region leaving the viewport would clear a region still in it. */
    this.reached = new Set();

    /* One observer over every target, as the reference does it: each entry updates its own
       part of the condition and the result is recomputed from the whole, so no target
       needs to know any other's state. */
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        /* `bottom < 0` rather than plain `!isIntersecting`: scrolling *above* the product
           — into the header — also stops the form intersecting, and the bar appearing
           there would offer the purchase before the shopper has reached the product. */
        if (entry.target === from) this.passed = entry.boundingClientRect.bottom < 0;
        else if (entry.isIntersecting) this.reached.add(entry.target);
        else this.reached.delete(entry.target);
      });

      const visible = Boolean(this.passed) && this.reached.size === 0;
      this.classList.toggle('is-visible', visible);
      /* `inert` rather than `aria-hidden` alone: a hidden bar must be unreachable by the
         Tab key too, or the focus order runs through invisible controls. */
      this.toggleAttribute('inert', !visible);
    });

    this.observer.observe(from);
    until.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
if (!customElements.get('sticky-buy-bar')) customElements.define('sticky-buy-bar', StickyBuyBar);
